import React, { useState, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import Checkbox from '../../Form/Checkbox';
import { colors, fontSize } from '../../../global/variables';
import { AVOID_ZONE_CATEGORIES, buildAvoidZonesPayload } from '../../../utils/avoidAreaHelpers';

const { secondaryTextColor, primaryTextColor, primaryBorderColor, secondaryBorderColor, aqua } =
  colors;
const { small, smaller } = fontSize;

const StyledSection = styled.details({
  backgroundColor: 'rgba(245,190,69,.03)',
  border: '1px solid rgba(245,190,69,.4)',
  borderRadius: '0.12rem',
  padding: '0.75rem',
  marginBottom: '0.9rem',
});

const StyledSummary = styled.summary({
  fontSize: small,
  fontWeight: 700,
  color: '#9C6B00',
  cursor: 'pointer',
});

const StyledSubTitle = styled.div({
  fontSize: smaller,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: secondaryTextColor,
  marginTop: '0.75rem',
  marginBottom: '0.4rem',
});

const StyledCategoryList = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.35rem',
});

const StyledCategoryCard = styled.div({
  border: `1px solid ${secondaryBorderColor}`,
  borderRadius: '0.12rem',
  backgroundColor: '#fff',
  overflow: 'hidden',
});

const StyledCategoryRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.32rem 0.5rem',
});

const StyledExceptPanel = styled.div({
  padding: '0.28rem 0.5rem 0.4rem 1.85rem',
  borderTop: `1px solid ${secondaryBorderColor}`,
  backgroundColor: 'rgba(0,0,0,.02)',
});

const StyledExceptLabel = styled.div({
  fontSize: smaller,
  color: secondaryTextColor,
  marginBottom: '0.25rem',
});

const StyledInputRow = styled.div({
  display: 'flex',
  gap: '0.3rem',
  marginBottom: '0.3rem',
});

const StyledZoneIdInput = styled.input({
  flex: 1,
  fontSize: smaller,
  padding: '0.25rem 0.45rem',
  border: `1px solid ${primaryBorderColor}`,
  borderRadius: '0.12rem',
  fontFamily: 'monospace',
});

const StyledAddButton = styled.button({
  flexShrink: 0,
  background: 'transparent',
  color: primaryTextColor,
  border: `1px solid ${primaryBorderColor}`,
  borderRadius: '0.12rem',
  padding: '0.22rem 0.55rem',
  fontSize: smaller,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'FiraGO',
  ':hover': { borderColor: aqua, color: aqua },
});

const StyledZoneTag = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.3rem',
  padding: '0.18rem 0.5rem 0.18rem 0.38rem',
  borderRadius: '1.5rem',
  fontSize: smaller,
  fontWeight: 500,
  margin: '0.15rem 0.3rem 0.15rem 0',
  backgroundColor: 'rgba(0,203,195,.1)',
  border: '1px solid rgba(0,203,195,.35)',
  color: '#00847E',
});

const StyledZoneTagRemove = styled.button({
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'inherit',
  opacity: 0.6,
  fontSize: smaller,
  padding: 0,
  lineHeight: 1,
  ':hover': { opacity: 1 },
});

const StyledEmptyLabel = styled.div({
  fontSize: smaller,
  color: primaryTextColor,
  fontStyle: 'italic',
  marginBottom: '0.4rem',
});

const StyledNoPreviewNote = styled.div({
  fontSize: smaller,
  color: secondaryTextColor,
  fontStyle: 'italic',
  marginBottom: '0.4rem',
});

const AvoidZonesEditor = ({
  avoid,
  onChange,
  labels: {
    avoidZonesTitle,
    avoidZoneCategoriesTitle,
    avoidZoneCategoriesLabels,
    avoidZoneExceptLabel,
    avoidZoneIdPlaceholder,
    avoidZoneAddLabel,
    avoidZoneRemoveTooltip,
    avoidSpecificZoneIdsTitle,
    avoidZoneIdentifiersEmptyLabel,
    avoidZoneNoPreviewNote,
  },
}) => {
  const [categories, setCategories] = useState(avoid?.zoneCategories?.categories || []);
  const [exceptZoneIds, setExceptZoneIds] = useState(avoid?.zoneCategories?.exceptZoneIds || []);
  const [zoneIdentifiers, setZoneIdentifiers] = useState(avoid?.zoneIdentifiers || []);
  const [exceptZoneIdInput, setExceptZoneIdInput] = useState('');
  const [zoneIdentifierInput, setZoneIdentifierInput] = useState('');

  useEffect(() => {
    onChange(buildAvoidZonesPayload({ categories, exceptZoneIds, zoneIdentifiers }));
  }, [categories, exceptZoneIds, zoneIdentifiers, onChange]);

  const onToggleCategory = useCallback((category) => {
    setCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category],
    );
  }, []);

  const onAddExceptZoneId = useCallback(() => {
    const value = exceptZoneIdInput.trim();
    if (!value) return;
    setExceptZoneIds((prev) => [...prev, value]);
    setExceptZoneIdInput('');
  }, [exceptZoneIdInput]);

  const onRemoveExceptZoneId = useCallback(
    (index) => setExceptZoneIds((prev) => prev.filter((_, i) => i !== index)),
    [],
  );

  const onAddZoneIdentifier = useCallback(() => {
    const value = zoneIdentifierInput.trim();
    if (!value) return;
    setZoneIdentifiers((prev) => [...prev, value]);
    setZoneIdentifierInput('');
  }, [zoneIdentifierInput]);

  const onRemoveZoneIdentifier = useCallback(
    (index) => setZoneIdentifiers((prev) => prev.filter((_, i) => i !== index)),
    [],
  );

  return (
    <StyledSection>
      <StyledSummary>{avoidZonesTitle}</StyledSummary>
      <StyledSubTitle>{avoidZoneCategoriesTitle}</StyledSubTitle>
      <StyledCategoryList>
        {AVOID_ZONE_CATEGORIES.map((category) => (
          <StyledCategoryCard key={category}>
            <StyledCategoryRow>
              <Checkbox
                id={`checkbox-fleet-profile-avoid-zone-category-${category}`}
                value={categories.includes(category)}
                label={avoidZoneCategoriesLabels[category]}
                onChange={() => onToggleCategory(category)}
              />
            </StyledCategoryRow>
            {category === 'environmental' && categories.includes('environmental') && (
              <StyledExceptPanel>
                <StyledExceptLabel>{avoidZoneExceptLabel}</StyledExceptLabel>
                <StyledInputRow>
                  <StyledZoneIdInput
                    type="text"
                    placeholder={avoidZoneIdPlaceholder}
                    value={exceptZoneIdInput}
                    onChange={(e) => setExceptZoneIdInput(e.target.value)}
                  />
                  <StyledAddButton
                    type="button"
                    id="button-fleet-profile-avoid-zone-except-add"
                    onClick={onAddExceptZoneId}
                  >
                    {avoidZoneAddLabel}
                  </StyledAddButton>
                </StyledInputRow>
                {exceptZoneIds.map((zoneId, index) => (
                  <StyledZoneTag key={`${zoneId}-${index}`}>
                    <code>{zoneId}</code>
                    <StyledZoneTagRemove
                      type="button"
                      id={`button-fleet-profile-avoid-zone-except-remove-${index}`}
                      title={avoidZoneRemoveTooltip}
                      onClick={() => onRemoveExceptZoneId(index)}
                    >
                      ×
                    </StyledZoneTagRemove>
                  </StyledZoneTag>
                ))}
              </StyledExceptPanel>
            )}
          </StyledCategoryCard>
        ))}
      </StyledCategoryList>
      <StyledSubTitle>{avoidSpecificZoneIdsTitle}</StyledSubTitle>
      <StyledNoPreviewNote>{avoidZoneNoPreviewNote}</StyledNoPreviewNote>
      <StyledInputRow>
        <StyledZoneIdInput
          type="text"
          placeholder={avoidZoneIdPlaceholder}
          value={zoneIdentifierInput}
          onChange={(e) => setZoneIdentifierInput(e.target.value)}
        />
        <StyledAddButton
          type="button"
          id="button-fleet-profile-avoid-zone-identifier-add"
          onClick={onAddZoneIdentifier}
        >
          {avoidZoneAddLabel}
        </StyledAddButton>
      </StyledInputRow>
      {zoneIdentifiers.length === 0 && (
        <StyledEmptyLabel>{avoidZoneIdentifiersEmptyLabel}</StyledEmptyLabel>
      )}
      {zoneIdentifiers.map((zoneId, index) => (
        <StyledZoneTag key={`${zoneId}-${index}`}>
          <code>{zoneId}</code>
          <StyledZoneTagRemove
            type="button"
            id={`button-fleet-profile-avoid-zone-identifier-remove-${index}`}
            title={avoidZoneRemoveTooltip}
            onClick={() => onRemoveZoneIdentifier(index)}
          >
            ×
          </StyledZoneTagRemove>
        </StyledZoneTag>
      ))}
    </StyledSection>
  );
};

export default AvoidZonesEditor;
